'use client';

import { forwardRef, type AnchorHTMLAttributes } from 'react';
import Link, { type LinkProps } from 'next/link';
import { useLocalePath } from '@/hooks/use-locale';

type LocaleLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof LinkProps
> &
  LinkProps & {
    children?: React.ReactNode;
    /**
     * Render a native <a> (full page load) instead of a client-side Next.js
     * Link. Use when the destination must start from a clean server-rendered
     * state — e.g. the "Search" nav link, so returning to the default search
     * doesn't reuse stale client state left on an already-open search page.
     */
    hardNavigate?: boolean;
  };

/**
 * Locale-aware Link component.
 * Accepts `href` as an internal path (e.g. "/search") and translates it
 * to a localized URL (e.g. "/de/suche") based on the current locale.
 */
export const LocaleLink = forwardRef<HTMLAnchorElement, LocaleLinkProps>(
  function LocaleLink({ href, hardNavigate, ...props }, ref) {
    const localePath = useLocalePath();

    // Split off the query string and hash before localizing. Otherwise the
    // first path segment ends up as "search?q=foo" and never matches the
    // SEGMENT_MAP entry for `search` — the URL stays on the English route
    // for German users, and middleware then 308-redirects it, which Search
    // Console reports as "Page with redirect".
    const localizedHref =
      typeof href === 'string'
        ? (() => {
            const sepIndex = href.search(/[?#]/);
            if (sepIndex === -1) return localePath(href);
            return `${localePath(href.slice(0, sepIndex))}${href.slice(sepIndex)}`;
          })()
        : href;

    if (hardNavigate) {
      // Drop Next.js Link-only props so they don't leak onto the native anchor.
      /* eslint-disable @typescript-eslint/no-unused-vars */
      const {
        prefetch,
        replace,
        scroll,
        shallow,
        locale,
        passHref,
        href: _ignoredHref,
        ...rest
      } = props as LinkProps & AnchorHTMLAttributes<HTMLAnchorElement>;
      /* eslint-enable @typescript-eslint/no-unused-vars */
      const nativeHref =
        typeof localizedHref === 'string' ? localizedHref : undefined;
      return <a ref={ref} href={nativeHref} {...rest} />;
    }

    return <Link ref={ref} href={localizedHref} {...props} />;
  },
);
